import {priority} from '@priority-software/priority-proto-api/lib';
import {formsStore} from '../../../stores/Form/FormsStore';
import {CachedFields} from '../../../stores/Form/FormsCache';
import {sendBatch} from '../../../features/Form/actions/form';
import {Options} from './server';

type INet2Grpc = priority.netitems.form.INet2Grpc;

type BatchTask = {
    key: string;
    request: unknown;
    index?: number;
    cacheData?: {
        key: string;
        formName: string;
        session?: string;
        cachedRs?: any;
    };
};

export class BatchRequest {
    tasks = [];
    pendingCacheTasksIndexes = [];
    session: string;

    constructor(session: string) {
        this.session = session;
    }

    addTask(params: BatchTask) {
        const task = {...params, index: this.tasks.length};

        if (params.cacheData) {
            const {key, formName, session} = params.cacheData;
            const cachedRs = formsStore.cachedData.getData(formName, key as CachedFields, session);

            if (!cachedRs) {
                this.pendingCacheTasksIndexes.push(task.index);
            }
            task.cacheData.cachedRs = cachedRs;
        }
        this.tasks.push(task);
    }

    async getData(options?: Partial<Options>) {
        const tasksData: {cachedTasks: Array<BatchTask>; serverTasks: Array<BatchTask>} =
            this.tasks.reduce(
                (acc, task) => {
                    const storage = task.cacheData?.cachedRs ? acc.cachedTasks : acc.serverTasks;
                    storage.push(task);

                    return acc;
                },
                {cachedTasks: [], serverTasks: []}
            );

        // Request data from server
        const res = await sendBatch(this.session, tasksData.serverTasks, options);

        const tasksResponses = res?.Tasks || [];
        const hasCachedData = tasksData.cachedTasks.length > 0;
        // Prepare final data response;
        let finalResults = !hasCachedData ? tasksResponses : [];

        // if we have cached data, we should insert it into correct position;
        if (hasCachedData) {
            const responseWithCache: Array<INet2Grpc & {cached?: boolean}> = [...tasksResponses];

            tasksData.cachedTasks.forEach(task => {
                const {cacheData, index, key} = task;

                const result =
                    index < this.tasks.length
                        ? {
                              [key]: cacheData.cachedRs,
                              cached: true
                          }
                        : null;

                responseWithCache.splice(index, 0, result);
            });

            finalResults = responseWithCache;
        }

        // store data to cache storage;
        this.pendingCacheTasksIndexes.forEach(index => {
            const responseToCache = finalResults[index];
            const currentTask = this.tasks[index];
            const cacheInfo = currentTask.cacheData;

            if (responseToCache) {
                const keyDataToCache = cacheInfo && responseToCache[currentTask.key];

                if (keyDataToCache && keyDataToCache.Messages.length === 0) {
                    formsStore.cachedData.setData(
                        cacheInfo.formName,
                        cacheInfo.key,
                        keyDataToCache,
                        cacheInfo.session
                    );
                }
            }
        });
        // Clear pending indexes;
        this.pendingCacheTasksIndexes = [];

        return finalResults;
    }
}
